import * as React from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import {useTheme} from '@mui/material/styles';

import type {ErrorObject} from 'ajv';
import {asJsonformsErrorTranslate, ReactState} from "./Forms";
import {JSONSchema7, JSONSchema7Definition} from "json-schema";
import {useTranslate} from "@tolgee/react";
import {Alert, Typography} from "@mui/material";
import Stack from "@mui/material/Stack";

// Define a type for the component props
interface FormExplanationDialogProps {
    openState: ReactState<boolean>,
    errors: ErrorObject[],
    schema: JSONSchema7,
    formNs: string
}

type FormExplanationRow = {
    path: string
    label: string | undefined,
    error: string | undefined,
    description: string | undefined

}

function isJSONSchema7(schema: JSONSchema7Definition): schema is JSONSchema7 {
    return (schema as JSONSchema7).type !== undefined;
}

export function FormExplanationDialog({openState, errors, schema, formNs}: FormExplanationDialogProps) {

    const [open, setOpen] = openState;

    const handleClose = () => {
        setOpen(false);
    };

    const {t} = useTranslate();

    const descriptionElementRef = React.useRef<HTMLElement>(null);
    React.useEffect(() => {
        if (open) {
            const {current: descriptionElement} = descriptionElementRef;
            if (descriptionElement !== null) {
                descriptionElement.focus();
            }
        }
    }, [open]);

    const rows: FormExplanationRow[] = [];

    const paths: string[] = [];

    const visitSchema = (schema: JSONSchema7, prefix: string) => {
        for (const key in schema.properties) {
            var property = schema.properties[key];
            if (property === undefined) {

            } else if ((isJSONSchema7(property) && property.type === 'object')) {
                visitSchema(property, prefix + key + ".");
            } else if ((isJSONSchema7(property) && property.type === 'array')) {
                paths.push(prefix + key);
                visitSchema(property.items as JSONSchema7, prefix + key + ".");
            } else {
                paths.push(prefix + key);
            }
        }
    };

    visitSchema(schema, formNs);

    paths.forEach(path => {
        rows.push({
            path: path,
            label: t(path + ".label"),
            error: "",
            description: t(path + ".description")
        });
    });

    const errorTranslate = asJsonformsErrorTranslate(t);

    errors.forEach(errorObject => {
        var path = errorObject.instancePath.replaceAll('/', '.');
        if (path.startsWith('.')) {
            path = path.substring(1);
        }
        path = formNs + path;

        if (errorObject.params["missingProperty"] !== undefined) {
            // a property is missing
            if (path.length > 0) {
                path += '.';
            }
            path += errorObject.params["missingProperty"];

            const row = rows.find(row => row.path === path);
            if (row) {
                row.error = errorTranslate(errorObject);
            }
        } else {
            // Another type of error
            const row = rows.find(row => row.path === path);
            if (row) {
                row.error = t(formNs + errorObject.message ?? "input_error");
            }
        }
    });

    const theme = useTheme();

    return (
        <React.Fragment>
            <Dialog
                open={open}
                onClose={handleClose}
                scroll={'paper'}
                aria-labelledby="form-explanation-dialog-title"
                aria-describedby="form-explanation-dialog-description"
                fullWidth={true}
                maxWidth="xl"
                closeAfterTransition={false}
            >
                <DialogTitle id="validation-dialog-title">
                    {t("form_explanation_dialog_title")}
                </DialogTitle>
                <DialogContent dividers={true}>
                    <Stack spacing={2}>
                        <Typography>
                            {t("form_explanation_dialog_description")}
                        </Typography>
                        <Alert severity={errors.length === 0 ? "success" : "error"} aria-label={"validation result"}>
                            {t(errors.length === 0 ? "form_validation_success" : "form_validation_failure")}
                        </Alert>

                        <TableContainer component={Paper}>
                            <Table aria-label="">
                                <TableHead style={{
                                    backgroundColor: theme.palette.grey[200],
                                }}>
                                    <TableRow>
                                        <TableCell
                                            style={{fontWeight: "bold"}}>{t("form_explanation_colum_field")}</TableCell>
                                        <TableCell
                                            style={{fontWeight: "bold"}}>{t("form_explanation_colum_error")}</TableCell>
                                        <TableCell
                                            style={{fontWeight: "bold"}}>{t("form_explanation_colum_description")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {
                                        rows.map((row) =>
                                            (<TableRow key={row.label}>
                                                <TableCell key="label">{row.label}</TableCell>
                                                <TableCell key="status">{row.error}</TableCell>
                                                <TableCell key="description">{row.description}</TableCell>
                                            </TableRow>)
                                        )
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>{t("ok")}</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
